/**
 * The width a box is laid out at, in whole CSS pixels — `null` until it has been measured.
 *
 * `present` says whether the box is mounted. The trajectory plot that reads this renders nothing on
 * a run with no points and its `<svg>` once there are some, without remounting, so a mount-only
 * effect would never see the element appear.
 *
 * The first reading is taken in a LAYOUT effect, so the plot is drawn at its real width on its first
 * painted frame and never seen re-laying itself out. The observer then follows the box as the window
 * or the day's dock changes size — a resize does not re-render the component on its own.
 *
 * It reads the LAYOUT width, not the bounding box: the day modal enters under a `scale()`, and a
 * transformed reading would draw the plot a little short for as long as no resize corrected it.
 */

import { useLayoutEffect, useState, type RefObject } from 'react';

export function useLaidOutWidth(
  ref: RefObject<Element | null>,
  present: boolean,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const box = ref.current;
    if (!present || box === null) return;

    const measure = () => {
      setWidth(Math.round(Number.parseFloat(getComputedStyle(box).width)) || null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  }, [ref, present]);

  return width;
}
